import { useTranslation } from 'react-i18next';
import { Clock, Globe } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { COMMON_TIMEZONES, DELIVERY_HOURS } from '@/constants/timezones';

interface DeliveryTimeSelectorProps {
  timezone: string;
  deliveryHour: number;
  onTimezoneChange: (timezone: string) => void;
  onDeliveryHourChange: (hour: number) => void;
  disabled?: boolean;
}

export const DeliveryTimeSelector = ({
  timezone,
  deliveryHour,
  onTimezoneChange,
  onDeliveryHourChange,
  disabled = false
}: DeliveryTimeSelectorProps) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4 p-4 rounded-lg bg-background/50 backdrop-blur-sm border border-primary/10
      hover:border-primary/30 transition-all duration-300">
      
      {/* Section Title */}
      <div className="flex items-center gap-2">
        <Clock className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold text-foreground">
          {t('preferences.deliveryTime.title')}
        </h3>
      </div>
      
      <p className="text-sm text-muted-foreground">
        {t('preferences.deliveryTime.description')}
      </p>

      {/* Timezone Selector */}
      <div className="space-y-2">
        <Label htmlFor="timezone" className="flex items-center gap-2">
          <Globe className="h-4 w-4" />
          {t('preferences.deliveryTime.timezone')}
        </Label>
        <Select
          value={timezone}
          onValueChange={onTimezoneChange}
          disabled={disabled}
        >
          <SelectTrigger id="timezone" className="w-full">
            <SelectValue placeholder={t('preferences.deliveryTime.selectTimezone')} />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {/* Group by region */}
            {['Americas', 'Europe', 'Asia', 'Oceania', 'Africa'].map((region) => (
              <div key={region}>
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                  {region}
                </div>
                {COMMON_TIMEZONES
                  .filter((tz) => tz.region === region)
                  .map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
              </div>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {t('preferences.deliveryTime.timezoneHint')}
        </p>
      </div>

      {/* Delivery Hour Selector */}
      <div className="space-y-3">
        <Label className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          {t('preferences.deliveryTime.preferredHour')}
        </Label>
        <RadioGroup
          value={String(deliveryHour)}
          onValueChange={(value) => onDeliveryHourChange(Number(value))}
          disabled={disabled}
          className="grid grid-cols-2 gap-3"
        >
          {DELIVERY_HOURS.map((hour) => (
            <div
              key={hour.value}
              className={`
                relative flex items-center space-x-2 rounded-lg border-2 p-4 cursor-pointer
                transition-all duration-200
                ${deliveryHour === hour.value
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50 hover:bg-accent/50'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <RadioGroupItem value={String(hour.value)} id={`hour-${hour.value}`} />
              <Label
                htmlFor={`hour-${hour.value}`}
                className="flex-1 cursor-pointer font-medium"
              >
                {hour.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
        <p className="text-xs text-muted-foreground">
          {t('preferences.deliveryTime.hourHint')}
        </p>
      </div>

      {/* Info Card */}
      <div className="p-3 rounded-md bg-primary/5 border border-primary/20">
        <p className="text-sm text-foreground flex items-start gap-2">
          <span className="text-lg">⏰</span>
          <span>
            {t('preferences.deliveryTime.reminder', {
              hour: DELIVERY_HOURS.find(h => h.value === deliveryHour)?.label || '7:00 AM',
              timezone: COMMON_TIMEZONES.find(tz => tz.value === timezone)?.label.split('(')[0].trim() || timezone
            })}
          </span>
        </p>
      </div>
    </div>
  );
};
